import React from 'react'
import { Image } from '@chakra-ui/react'
import { useDisclosure } from '@chakra-ui/react'
import { Box, Wrap, WrapItem } from '@chakra-ui/react'

import ImagePreviewModal from '~components/shared/modals/image-preview-modal'
import PdfAttachment from '~components/chats/chat/messages-list/attachment-preview/pdf'
import {
    Attachment,
    AttachmentType,
    InternalChatMessageAttachmentFieldsFragment,
} from '~graphql/generated/graphql'
import { FaCirclePlay } from 'react-icons/fa6'
import { Icon } from '@chakra-ui/icons'
import { Center } from '@chakra-ui/react'

interface Props {
    attachments:
        | InternalChatMessageAttachmentFieldsFragment[]
        | null
        | undefined
}
export default function TeamChatMessageAttachments({ attachments }: Props) {
    if (!attachments || attachments.length === 0) return null

    return (
        <Wrap w="full" gridGap={4} mt={2}>
            {attachments.map(attachment => (
                <WrapItem key={attachment.id}>
                    <TeamChatAttachmentItem attachment={attachment} />
                </WrapItem>
            ))}
        </Wrap>
    )
}

function TeamChatAttachmentItem({
    attachment,
}: {
    attachment: InternalChatMessageAttachmentFieldsFragment
}) {
    const { isOpen, onOpen, onClose } = useDisclosure()

    if (attachment.type === AttachmentType.Pdf) {
        const pdfAttachment: Attachment = {
            id: attachment.id,
            url: attachment.url,
            filename: attachment.filename,
            type: AttachmentType.Pdf,
        }

        return (
            <Box h={24}>
                <PdfAttachment attachment={pdfAttachment} />
            </Box>
        )
    }

    return (
        <>
            <Box
                borderWidth={1}
                borderColor="gray.200"
                bg="gray.200"
                cursor="pointer"
                rounded="xl"
                overflow="hidden"
                _hover={{
                    filter: 'brightness(0.9)',
                }}
                transition="filter 0.1s ease-in-out"
                onClick={onOpen}
            >
                {attachment.type === AttachmentType.Video ? (
                    <VideoAttachment attachment={attachment} />
                ) : (
                    <ImageAttachment attachment={attachment} />
                )}
            </Box>
            <ImagePreviewModal
                isOpen={isOpen}
                onClose={onClose}
                url={attachment.url}
                type={attachment.type as AttachmentType}
            />
        </>
    )
}

function ImageAttachment({
    attachment,
}: {
    attachment: InternalChatMessageAttachmentFieldsFragment
}) {
    return (
        <Image
            src={attachment.url}
            alt={attachment.filename}
            w="auto"
            h={24}
            objectFit="cover"
        />
    )
}

function VideoAttachment({
    attachment,
}: {
    attachment: InternalChatMessageAttachmentFieldsFragment
}) {
    return (
        <Box
            bg="gray.100"
            overflow="hidden"
            borderRadius="lg"
            position="relative"
        >
            <Box
                as="video"
                src={attachment.url}
                height={24}
                w="auto"
                objectFit="contain"
            />
            <Center
                position="absolute"
                top={0}
                right={0}
                left={0}
                bottom={0}
                bg="blackAlpha.200"
            >
                <Icon as={FaCirclePlay} fontSize="5xl" color="whiteAlpha.700" />
            </Center>
        </Box>
    )
}
